import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { Avatar } from '../components/Logo';
import { Plus, X, Trash2, Loader2, ChevronLeft, ChevronRight, Check, Target, Briefcase } from '../components/icons';
import FoundersLeaderboard from '../components/FoundersLeaderboard';

const SCOPES = [
  { id: 'company',   label: 'Objectif de la boîte', icon: Briefcase, color: '#5BBEFC' },
  { id: 'founders',  label: 'Founders',             icon: Target,    color: '#A78BFA' },
  { id: 'editor',    label: 'Editor',               icon: Target,    color: '#34D399' },
  { id: 'personal',  label: 'Personnel',            icon: Target,    color: '#F5A623' },
];

function isoMonday(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatRange(monday) {
  const start = new Date(monday);
  const end = new Date(monday); end.setDate(end.getDate() + 6);
  const opts = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString('fr-FR', opts)} → ${end.toLocaleDateString('fr-FR', opts)}`;
}

export default function ObjectivesPage() {
  const { user, isFounder } = useAuth();
  const [weekStart, setWeekStart] = useState(() => isoMonday(new Date()));
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month' | 'quarter'
  const [objectives, setObjectives] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // scope id
  const [showWeekPicker, setShowWeekPicker] = useState(false);

  const weekKey = weekStart.toISOString().slice(0, 10);

  const reload = async () => {
    setLoading(true);
    // Iter62r — In week mode, filter by exact week_start. In month/quarter, load all
    // and filter client-side to the current period range.
    const url = viewMode === 'week' ? `/objectives?week_start=${weekKey}` : '/objectives';
    const [o, u] = await Promise.all([
      api.get(url),
      api.get('/users'),
    ]);
    setObjectives(o.data); setUsers(u.data); setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [weekKey, viewMode]);

  // Iter62r — Range for month/quarter modes
  const periodRange = useMemo(() => {
    if (viewMode === 'week') return null;
    const start = new Date(weekStart);
    if (viewMode === 'month') start.setDate(start.getDate() - start.getDay() * 0); // keep same monday
    // for month: N weeks going back to the start of current month + 3 weeks forward
    const end = new Date(weekStart);
    const nWeeks = viewMode === 'month' ? 4 : 12;
    start.setDate(start.getDate() - Math.floor(nWeeks / 2) * 7);
    end.setDate(end.getDate() + Math.ceil(nWeeks / 2) * 7 + 6);
    return { start, end, nWeeks };
  }, [weekStart, viewMode]);

  const scopedObjectives = useMemo(() => {
    if (viewMode === 'week') return objectives;
    if (!periodRange) return objectives;
    const sIso = periodRange.start.toISOString().slice(0, 10);
    const eIso = periodRange.end.toISOString().slice(0, 10);
    return objectives.filter((o) => {
      const w = o.week_start || '';
      return w >= sIso && w <= eIso;
    });
  }, [objectives, viewMode, periodRange]);

  const byScope = useMemo(() => {
    const m = { company: [], founders: [], editor: [], personal: [] };
    scopedObjectives.forEach((o) => {
      if (o.scope === 'personal') {
        // Iter61ad — personal reste strictement personnel (founders inclus)
        if (o.owner === user.id) m.personal.push(o);
      } else {
        (m[o.scope] || m.company).push(o);
      }
    });
    return m;
  }, [scopedObjectives, user.id, isFounder]);

  const moveWeek = (n) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + n * 7);
    setWeekStart(d);
  };

  return (
    <div className="page-fade">
      <PageHeader
        title="Objectifs de la semaine"
        subtitle="Aligne l'équipe sur ce qui compte cette semaine"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Iter62r — Period switch (Semaine / Mois / Trimestre) */}
            <div
              data-testid="obj-period-switch"
              style={{
                display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10,
                background: 'var(--sf)', border: '1px solid var(--bd)',
              }}
            >
              {[
                { k: 'week', label: 'Semaine' },
                { k: 'month', label: 'Mois' },
                { k: 'quarter', label: 'Trimestre' },
              ].map((p) => (
                <button
                  key={p.k}
                  data-testid={`obj-period-${p.k}`}
                  onClick={() => setViewMode(p.k)}
                  style={{
                    padding: '5px 12px', borderRadius: 8,
                    background: viewMode === p.k
                      ? 'linear-gradient(135deg, rgba(127,173,255,0.22), rgba(62,123,250,0.10))'
                      : 'transparent',
                    border: `1px solid ${viewMode === p.k ? 'rgba(127,173,255,0.42)' : 'transparent'}`,
                    color: viewMode === p.k ? '#B8D4FF' : 'var(--t3)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem',
                    cursor: 'pointer',
                    boxShadow: viewMode === p.k ? 'inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
                  }}
                >{p.label}</button>
              ))}
            </div>

            {viewMode === 'week' && (
              <>
                <button onClick={() => moveWeek(-1)} style={navBtnStyle} data-testid="obj-week-prev">
                  <ChevronLeft size={14} color="var(--t2)" />
                </button>
                <div style={{ position: 'relative' }}>
                  <button
                    data-testid="obj-week-picker-toggle"
                    onClick={() => setShowWeekPicker((v) => !v)}
                    style={{
                      padding: '7px 12px', borderRadius: 10,
                      background: 'var(--pbg)', border: '1px solid var(--pbd)',
                      fontFamily: 'Sora', fontWeight: 600, fontSize: '0.74rem', color: 'var(--p)',
                      cursor: 'pointer',
                    }}
                  >Cette semaine</button>
                  {showWeekPicker && (
                    <WeekPickerPopover
                      selected={weekStart}
                      onPick={(d) => { setWeekStart(d); setShowWeekPicker(false); }}
                      onClose={() => setShowWeekPicker(false)}
                    />
                  )}
                </div>
                <button onClick={() => moveWeek(1)} style={navBtnStyle} data-testid="obj-week-next">
                  <ChevronRight size={14} color="var(--t2)" />
                </button>
              </>
            )}
          </div>
        }
      />

      <div style={{ padding: '0 22px 8px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="asa-eyebrow" style={{ color: 'var(--pl)' }}>
          {viewMode === 'week'
            ? formatRange(weekStart)
            : viewMode === 'month'
              ? `Mois glissant · ${periodRange ? formatRange(periodRange.start) : ''} …`
              : `Trimestre · ${periodRange ? formatRange(periodRange.start) : ''} …`}
        </span>
        {/* Iter61ad — Weekly tracking stats */}
        {scopedObjectives.length > 0 && (
          <div data-testid="obj-week-stats" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '3px 10px', borderRadius: 999,
            background: 'var(--sf2)', border: '1px solid var(--bd)',
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem',
          }}>
            <span style={{ color: '#34D399' }}>
              ✓ {scopedObjectives.filter((o) => o.done).length} réussi{scopedObjectives.filter((o) => o.done).length > 1 ? 's' : ''}
            </span>
            <span style={{ color: 'var(--t4)' }}>·</span>
            <span style={{ color: '#ef4444' }}>
              ✗ {scopedObjectives.filter((o) => o.failed).length} échoué{scopedObjectives.filter((o) => o.failed).length > 1 ? 's' : ''}
            </span>
            <span style={{ color: 'var(--t4)' }}>·</span>
            <span style={{ color: 'var(--t2)' }}>
              ⏳ {scopedObjectives.filter((o) => !o.done && !o.failed).length} en cours
            </span>
          </div>
        )}
      </div>

      <div style={{ padding: '14px 22px 100px' }}>
        {isFounder && <FoundersLeaderboard />}
        {viewMode !== 'week' ? (
          <PeriodOverview
            mode={viewMode}
            objectives={scopedObjectives}
            users={users}
            currentUserId={user.id}
            range={periodRange}
            onJumpToWeek={(iso) => {
              setViewMode('week');
              setWeekStart(new Date(`${iso}T00:00:00`));
            }}
          />
        ) : loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="shimmer" style={{ height: 200, borderRadius: 16 }} />)}
          </div>
        ) : (
          <div className="stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {SCOPES.map((sc) => {
              if (sc.id === 'editor' && user.role === 'founder') {
                // founders see editor scope too
              }
              if (sc.id === 'founders' && !isFounder) return null;
              return (
                <ScopeCard
                  key={sc.id}
                  scope={sc}
                  items={byScope[sc.id] || []}
                  users={users}
                  weekKey={weekKey}
                  isFounder={isFounder}
                  currentUserId={user.id}
                  onAdd={() => setAdding(sc.id)}
                  onChanged={reload}
                />
              );
            })}
          </div>
        )}
      </div>

      {adding && (
        <AddObjectiveDialog
          scope={adding}
          weekKey={weekKey}
          users={users}
          onClose={() => setAdding(null)}
          onSaved={() => { setAdding(null); reload(); }}
        />
      )}
    </div>
  );
}

const navBtnStyle = {
  width: 32, height: 32, borderRadius: 8,
  background: 'var(--sf)', border: '1.5px solid var(--bd)',
  display: 'grid', placeItems: 'center',
};

// Iter62r — Compact week picker popover: lets you jump to any Monday (past or future).
function WeekPickerPopover({ selected, onPick, onClose }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(selected);
    d.setDate(1);
    return d;
  });

  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  // Build 6 weeks of the cursor month
  const gridStart = new Date(cursor);
  gridStart.setDate(1);
  const off = (gridStart.getDay() + 6) % 7;
  gridStart.setDate(gridStart.getDate() - off);

  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const monday = new Date(gridStart);
    monday.setDate(gridStart.getDate() + w * 7);
    weeks.push(monday);
  }
  const selKey = isoMonday(selected).toISOString().slice(0, 10);
  const now = isoMonday(new Date()).toISOString().slice(0, 10);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 240 }} />
      <div
        data-testid="obj-week-picker"
        style={{
          position: 'absolute', top: 44, right: 0, zIndex: 241,
          width: 296, padding: 14, borderRadius: 16,
          background:
            'radial-gradient(120% 100% at 0% 0%, rgba(127,173,255,0.16) 0%, rgba(127,173,255,0) 55%),' +
            ' linear-gradient(160deg, rgba(28,38,80,0.94) 0%, rgba(16,24,58,0.98) 100%)',
          border: '1px solid rgba(127,173,255,0.28)',
          boxShadow: '0 28px 60px -18px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <button
            onClick={() => { const n = new Date(cursor); n.setMonth(n.getMonth() - 1); setCursor(n); }}
            style={{ ...navBtnStyle, width: 26, height: 26 }}
          ><ChevronLeft size={12} color="var(--t2)" /></button>
          <div style={{ flex: 1, textAlign: 'center', textTransform: 'capitalize', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)' }}>{monthLabel}</div>
          <button
            onClick={() => { const n = new Date(cursor); n.setMonth(n.getMonth() + 1); setCursor(n); }}
            style={{ ...navBtnStyle, width: 26, height: 26 }}
          ><ChevronRight size={12} color="var(--t2)" /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
          {['L','M','M','J','V','S','D'].map((c, i) => (
            <div key={`h${i}`} style={{ textAlign: 'center', fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: 'var(--t3)', textTransform: 'uppercase' }}>{c}</div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {weeks.map((monday, wi) => {
            const wIso = monday.toISOString().slice(0, 10);
            const isSel = wIso === selKey;
            const isCur = wIso === now;
            return (
              <button
                key={`w${wi}`}
                data-testid={`obj-week-pick-${wIso}`}
                onClick={() => onPick(monday)}
                style={{
                  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
                  padding: 3, borderRadius: 8,
                  background: isSel
                    ? 'linear-gradient(135deg, rgba(127,173,255,0.22), rgba(62,123,250,0.10))'
                    : isCur
                      ? 'rgba(245,166,35,0.08)'
                      : 'transparent',
                  border: `1px solid ${isSel ? 'rgba(127,173,255,0.42)' : isCur ? 'rgba(245,166,35,0.20)' : 'transparent'}`,
                  cursor: 'pointer',
                }}
              >
                {Array.from({ length: 7 }).map((_, di) => {
                  const day = new Date(monday); day.setDate(day.getDate() + di);
                  const isCurrentMonth = day.getMonth() === cursor.getMonth();
                  return (
                    <div key={di} style={{
                      textAlign: 'center', padding: '5px 0',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                      color: isSel ? '#B8D4FF' : isCurrentMonth ? 'var(--t2)' : 'var(--t4)',
                    }}>{day.getDate()}</div>
                  );
                })}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}


// Iter62r — Period Overview (mois/trimestre): stacked weekly buckets with per-scope objective counts.
function PeriodOverview({ mode, objectives, users, currentUserId, range, onJumpToWeek }) {
  const buckets = useMemo(() => {
    if (!range) return [];
    const start = new Date(range.start);
    const list = [];
    const n = range.nWeeks;
    for (let i = 0; i < n; i++) {
      const monday = new Date(start);
      monday.setDate(start.getDate() + i * 7);
      const iso = monday.toISOString().slice(0, 10);
      const items = objectives.filter((o) => o.week_start === iso);
      list.push({
        iso, monday,
        items,
        done: items.filter((o) => o.done).length,
        failed: items.filter((o) => o.failed).length,
        pending: items.filter((o) => !o.done && !o.failed).length,
      });
    }
    return list;
  }, [range, objectives]);

  return (
    <div
      data-testid="obj-period-overview"
      style={{
        borderRadius: 18, padding: '18px 20px 20px',
        background:
          'radial-gradient(120% 100% at 0% 0%, rgba(127,173,255,0.10) 0%, rgba(127,173,255,0) 55%),' +
          ' linear-gradient(160deg, rgba(28,38,80,0.60) 0%, rgba(16,24,58,0.85) 100%)',
        border: '1px solid rgba(127,173,255,0.20)',
        boxShadow: '0 18px 34px -18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--t3)', marginBottom: 12 }}>
        {mode === 'month' ? 'Vue mensuelle' : 'Vue trimestrielle'} · {buckets.length} semaines
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {buckets.map((b) => (
          <button
            key={b.iso}
            data-testid={`obj-bucket-${b.iso}`}
            onClick={() => onJumpToWeek(b.iso)}
            style={{
              display: 'grid', gridTemplateColumns: '110px 1fr auto', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              textAlign: 'left', cursor: 'pointer',
              transition: 'background 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,173,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(127,173,255,0.24)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
          >
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: 'var(--t1)' }}>
                Sem. du {b.monday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 10, color: 'var(--t3)', marginTop: 1 }}>
                {b.items.length} objectif{b.items.length > 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', minWidth: 0 }}>
              {b.items.slice(0, 4).map((o) => (
                <div key={o.id} style={{
                  padding: '3px 8px', borderRadius: 6,
                  background: o.done ? 'rgba(52,211,153,0.10)' : o.failed ? 'rgba(239,68,68,0.10)' : 'rgba(127,173,255,0.06)',
                  border: `1px solid ${o.done ? 'rgba(52,211,153,0.30)' : o.failed ? 'rgba(239,68,68,0.30)' : 'rgba(127,173,255,0.20)'}`,
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                  color: o.done ? '#34D399' : o.failed ? '#ef4444' : 'var(--t2)',
                  maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{o.text}</div>
              ))}
              {b.items.length > 4 && (
                <div style={{
                  padding: '3px 8px', borderRadius: 6,
                  background: 'transparent', border: '1px dashed rgba(255,255,255,0.15)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: 'var(--t3)',
                }}>+{b.items.length - 4}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, fontFamily: 'Sora', fontWeight: 800, fontSize: 11 }}>
              <span style={{ color: '#34D399' }}>✓ {b.done}</span>
              <span style={{ color: '#ef4444' }}>✗ {b.failed}</span>
              <span style={{ color: 'var(--t3)' }}>⏳ {b.pending}</span>
            </div>
          </button>
        ))}
      </div>
      <div style={{
        marginTop: 12, padding: '10px 12px', borderRadius: 10,
        background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.18)',
        fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t2)',
      }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 800, color: '#F5A623', marginRight: 5 }}>Astuce</span>
        Clique sur une semaine pour zoomer dessus et créer/éditer les objectifs.
      </div>
    </div>
  );
}


function ScopeCard({ scope, items, users, isFounder, currentUserId, onAdd, onChanged }) {
  const Icon = scope.icon;
  return (
    <div
      style={{
        position: 'relative',
        padding: 18,
        borderRadius: 16,
        overflow: 'hidden',
        background:
          'radial-gradient(120% 100% at 100% 0%, rgba(245,166,35,0.10) 0%, rgba(245,166,35,0) 55%),' +
          ' linear-gradient(160deg, rgba(38,48,90,0.85) 0%, rgba(22,32,64,0.95) 100%)',
        border: '1.5px solid rgba(245,166,35,0.22)',
        boxShadow:
          '0 14px 30px -14px rgba(0,0,0,0.60),' +
          ' inset 0 1px 0 rgba(255,255,255,0.06),' +
          ' inset 0 -20px 40px -20px rgba(245,166,35,0.10)',
      }}
    >
      {/* Iter62w — Metallic top sheen */}
      <span style={{
        position: 'absolute', top: 0, left: 6, right: 6, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(245,166,35,0.55), transparent)',
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: `linear-gradient(135deg, ${scope.color}33, ${scope.color}0F)`,
          border: `1.5px solid ${scope.color}66`,
          boxShadow: `0 6px 14px -6px ${scope.color}55, inset 0 1px 0 rgba(255,255,255,0.15)`,
          display: 'grid', placeItems: 'center',
        }}>
          <Icon size={15} color={scope.color} strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '0.94rem', color: 'var(--t1)', letterSpacing: '-0.01em' }}>
            {scope.label}
          </div>
          <div className="asa-eyebrow" style={{ fontSize: '0.6rem', marginTop: 2 }}>
            {items.length} objectif{items.length > 1 ? 's' : ''}
          </div>
        </div>
        <button
          data-testid={`obj-add-${scope.id}`}
          onClick={onAdd}
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'linear-gradient(135deg, rgba(245,166,35,0.18), rgba(245,166,35,0.06))',
            border: '1px solid rgba(245,166,35,0.36)',
            boxShadow: '0 4px 10px -4px rgba(245,166,35,0.35), inset 0 1px 0 rgba(255,255,255,0.10)',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
          }}
        >
          <Plus size={13} color="#F5D08A" />
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{
          padding: '18px 12px', textAlign: 'center',
          color: 'var(--t3)', fontFamily: 'Lexend', fontSize: '0.78rem', fontStyle: 'italic',
        }}>
          Aucun objectif défini
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it) => (
            <ObjectiveRow
              key={it.id}
              obj={it}
              users={users}
              scope={scope}
              canDelete={isFounder || it.created_by === currentUserId}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ObjectiveRow({ obj, users, scope, canDelete, onChanged }) {
  const owner = users.find((u) => u.id === obj.owner);
  const modifier = users.find((u) => u.id === obj.modified_by);
  const assignee = users.find((u) => u.id === obj.assignee);
  const assigneeColor = obj.assignee === 'rayan' ? '#F5A623' : obj.assignee === 'hugo' ? '#5BBEFC' : 'var(--t3)';
  const toggleDone = async () => {
    try { await api.patch(`/objectives/${obj.id}`, { done: !obj.done }); onChanged(); }
    catch { toast.error('Erreur'); }
  };
  // Iter61ad — Objective can also be marked as FAILED (mutually exclusive with done)
  const toggleFailed = async () => {
    try { await api.patch(`/objectives/${obj.id}`, { failed: !obj.failed }); onChanged(); }
    catch { toast.error('Erreur'); }
  };
  const remove = async () => {
    if (!window.confirm('Supprimer cet objectif ?')) return;
    try { await api.delete(`/objectives/${obj.id}`); onChanged(); }
    catch { toast.error('Erreur'); }
  };
  return (
    <div style={{
      position: 'relative',
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', borderRadius: 12,
      background: obj.failed
        ? 'linear-gradient(160deg, rgba(60,30,32,0.75), rgba(40,20,22,0.90))'
        : 'linear-gradient(160deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))',
      border: obj.failed
        ? '1px solid rgba(239,68,68,0.32)'
        : assignee ? `1px solid ${assigneeColor}44` : '1px solid rgba(245,166,35,0.14)',
      borderLeft: obj.failed
        ? '3px solid #ef4444'
        : assignee ? `3px solid ${assigneeColor}` : '3px solid rgba(245,166,35,0.35)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
      opacity: obj.failed ? 0.85 : 1,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', marginTop: 1 }}>
        <button
          onClick={toggleDone}
          data-testid={`obj-done-${obj.id}`}
          title={obj.done ? 'Marquer non fait' : 'Marquer réussi'}
          style={{
            width: 18, height: 18, borderRadius: 5,
            background: obj.done ? scope.color : 'transparent',
            border: `1.5px solid ${scope.color}`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          {obj.done && <Check size={11} color="#fff" strokeWidth={3} />}
        </button>
        {/* Iter61ad — Fail button */}
        <button
          onClick={toggleFailed}
          data-testid={`obj-failed-${obj.id}`}
          title={obj.failed ? 'Retirer l\'échec' : 'Marquer échoué'}
          style={{
            width: 18, height: 18, borderRadius: 5,
            background: obj.failed ? '#ef4444' : 'transparent',
            border: '1.5px solid #ef4444',
            display: 'grid', placeItems: 'center', flexShrink: 0,
            cursor: 'pointer',
            opacity: obj.done ? 0.35 : 1,
          }}
        >
          {obj.failed && <X size={11} color="#fff" strokeWidth={3} />}
        </button>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Lexend', fontSize: '0.86rem',
          color: (obj.done || obj.failed) ? 'var(--t3)' : 'var(--t1)',
          textDecoration: (obj.done || obj.failed) ? 'line-through' : 'none',
          lineHeight: 1.4,
        }}>
          {obj.text}
        </div>

        {/* Iter62r — Weekday chips (personal targeted days) */}
        {Array.isArray(obj.weekdays) && obj.weekdays.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {obj.weekdays.map((d) => (
              <span key={d} style={{
                padding: '2px 7px', borderRadius: 999,
                background: 'rgba(127,173,255,0.10)',
                border: '1px solid rgba(127,173,255,0.28)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                color: '#B8D4FF', letterSpacing: '0.03em', textTransform: 'uppercase',
              }}>{['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'][d] || `J${d}`}</span>
            ))}
          </div>
        )}

        {/* Iter60d — show sub-steps if any */}
        {Array.isArray(obj.steps) && obj.steps.length > 0 && (
          <div style={{
            marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4,
            paddingLeft: 4,
          }}>
            {obj.steps.map((s, i) => {
              const toggleStep = async () => {
                try {
                  const nextSteps = obj.steps.map((x, j) => j === i ? { ...x, done: !x.done } : x);
                  await api.patch(`/objectives/${obj.id}`, { steps: nextSteps });
                  onChanged();
                } catch { /* silent */ }
              };
              return (
                <div key={s.id || i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={toggleStep}
                    data-testid={`obj-step-toggle-${obj.id}-${i}`}
                    style={{
                      width: 13, height: 13, borderRadius: 3, cursor: 'pointer', flexShrink: 0,
                      background: s.done ? '#5BBEFC' : 'transparent',
                      border: '1.3px solid #5BBEFC',
                      display: 'grid', placeItems: 'center', padding: 0,
                    }}
                  >
                    {s.done && <Check size={8} color="#fff" strokeWidth={3.5} />}
                  </button>
                  <span style={{
                    fontFamily: 'Lexend', fontSize: 11.5,
                    color: s.done ? 'var(--t3)' : 'var(--t2)',
                    textDecoration: s.done ? 'line-through' : 'none',
                    lineHeight: 1.35,
                  }}>{s.text}</span>
                </div>
              );
            })}
          </div>
        )}
        {(owner || modifier || assignee) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
            {assignee && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 999,
                background: `${assigneeColor}1F`, border: `1px solid ${assigneeColor}55`,
              }}>
                <Avatar user={assignee} size={12} />
                <span style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
                  color: assigneeColor, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>{assignee.name.split(' ')[0]}</span>
              </span>
            )}
            {owner && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Avatar user={owner} size={14} />
                <span style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)' }}>{owner.name.split(' ')[0]}</span>
              </span>
            )}
            {modifier && !owner && !assignee && (
              <span style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)' }}>
                modifié par {modifier.name.split(' ')[0]}
              </span>
            )}
          </div>
        )}
      </div>
      {canDelete && (
        <button onClick={remove} style={{ padding: 4, borderRadius: 6 }}>
          <Trash2 size={12} color="var(--t3)" />
        </button>
      )}
    </div>
  );
}

// Pill button for assignee picker (yellow for Rayan, blue for Hugo)
function AssigneeChip({ testid, label, color, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 999,
        background: active ? `${color}1F` : 'var(--sf2)',
        border: `1.5px solid ${active ? color : 'var(--bd)'}`,
        color: active ? color : 'var(--t3)',
        fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      {label}
    </button>
  );
}

function AddObjectiveDialog({ scope, weekKey, users, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [owner, setOwner] = useState('');
  const [assignee, setAssignee] = useState('');
  const [steps, setSteps] = useState(['']); // Iter60d — required sub-steps
  const [weekdays, setWeekdays] = useState([]); // Iter62r — personal targeted weekdays (0=Mon..6=Sun)
  const [busy, setBusy] = useState(false);

  const nonEmptySteps = steps.map((s) => s.trim()).filter(Boolean);
  const canSubmit = text.trim() && nonEmptySteps.length > 0;

  const toggleDay = (d) => {
    setWeekdays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) { toast.error('Objectif requis'); return; }
    if (nonEmptySteps.length === 0) { toast.error('Ajoute au moins une sous-tâche pour décomposer l\'objectif'); return; }
    setBusy(true);
    try {
      await api.post('/objectives', {
        text: text.trim(), scope, week_start: weekKey,
        owner: scope === 'personal' ? (owner || null) : null,
        assignee: assignee || null,
        weekdays: scope === 'personal' && weekdays.length > 0 ? weekdays : null,
        steps: nonEmptySteps.map((s, i) => ({
          id: `${Date.now()}-${i}`,
          text: s,
          done: false,
        })),
      });
      toast.success('Objectif ajouté');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  // Only show founders in the assignee picker (yellow=Rayan, blue=Hugo per request)
  const founders = users.filter((u) => u.role === 'founder');

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <form
        onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 460,
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22, padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouvel objectif
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>

        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Objectif</div>
        <textarea
          data-testid="obj-text"
          autoFocus rows={3}
          placeholder="Ex: Publier 3 reels sur le compte Eleve"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{
            width: '100%', padding: '12px 14px', resize: 'vertical',
            background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
            fontFamily: 'Lexend', fontSize: '0.92rem', color: 'var(--t1)', lineHeight: 1.5,
            marginBottom: 14,
          }}
        />

        {/* Iter60d — Mandatory sub-steps to decompose the goal */}
        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
          Étapes pour l&apos;atteindre <span style={{ color: '#EF4444' }}>*</span>
        </div>
        <div style={{
          background: 'var(--bg)', border: '1.5px solid var(--bd)', borderRadius: 12,
          padding: 10, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <div style={{
                width: 22, height: 22, borderRadius: 6,
                background: 'rgba(91,190,252,0.15)', border: '1px solid rgba(91,190,252,0.30)',
                display: 'grid', placeItems: 'center', flexShrink: 0,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 10, color: '#5BBEFC',
              }}>{i + 1}</div>
              <input
                data-testid={`obj-step-${i}`}
                type="text"
                value={s}
                onChange={(e) => {
                  const next = [...steps];
                  next[i] = e.target.value;
                  setSteps(next);
                }}
                placeholder={`Étape ${i + 1}`}
                style={{
                  flex: 1, padding: '9px 12px',
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 8,
                  fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                }}
              />
              {steps.length > 1 && (
                <button
                  type="button"
                  data-testid={`obj-step-remove-${i}`}
                  onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                  title="Retirer"
                  style={{
                    width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
                    background: 'rgba(255,107,107,0.10)', border: '1px solid rgba(255,107,107,0.30)',
                    color: '#FF6B6B', display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}
                ><X size={12} /></button>
              )}
            </div>
          ))}
          <button
            type="button"
            data-testid="obj-step-add"
            onClick={() => setSteps([...steps, ''])}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
              padding: '6px 12px', borderRadius: 8, marginTop: 4,
              background: 'rgba(91,190,252,0.10)', border: '1px dashed rgba(91,190,252,0.35)',
              color: '#5BBEFC', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer',
            }}
          >+ Ajouter une étape</button>
        </div>

        {scope === 'personal' && (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Pour</div>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              style={{
                width: '100%', padding: '11px 14px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 12,
                fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t1)', marginBottom: 14,
              }}
            >
              <option value="">Choisir un membre</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>

            {/* Iter62r — Weekday selector (optional): which days of the week the objective targets */}
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Jours ciblés (optionnel)</div>
            <div data-testid="obj-weekdays" style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((lbl, i) => {
                const on = weekdays.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`obj-weekday-${i}`}
                    onClick={() => toggleDay(i)}
                    style={{
                      minWidth: 40, padding: '7px 10px', borderRadius: 10,
                      background: on
                        ? 'linear-gradient(135deg, rgba(127,173,255,0.24), rgba(62,123,250,0.10))'
                        : 'var(--sf2)',
                      border: `1.5px solid ${on ? 'rgba(127,173,255,0.48)' : 'var(--bd)'}`,
                      color: on ? '#B8D4FF' : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                      cursor: 'pointer',
                    }}
                  >{lbl}</button>
                );
              })}
            </div>
          </>
        )}

        {/* Assignee picker — yellow for Rayan, blue for Hugo */}
        {founders.length > 0 && scope !== 'personal' && (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Assigné à</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <AssigneeChip
                testid="assignee-none"
                label="Personne"
                color="var(--t3)"
                active={!assignee}
                onClick={() => setAssignee('')}
              />
              {founders.map((u) => (
                <AssigneeChip
                  key={u.id}
                  testid={`assignee-${u.id}`}
                  label={u.name.split(' ')[0]}
                  color={u.id === 'rayan' ? '#F5A623' : '#5BBEFC'}
                  active={assignee === u.id}
                  onClick={() => setAssignee(u.id)}
                />
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="obj-submit" type="submit" disabled={busy || !canSubmit} className="asa-btn" style={{ flex: 1 }}>
            {busy ? <Loader2 size={15} className="spin" /> : 'Ajouter'}
          </button>
        </div>
      </form>
    </div>
  );
}
